// src/portals/admin/components/FanDetailModal.js
import React, { useState } from "react";
import { X, User, DollarSign, Calendar, Heart, Shield, ShoppingBag, Music } from "lucide-react";
import Card from "../../../components/ui/Card";
import Button from "../../../components/ui/Button";

const FanDetailModal = ({ fan, onClose }) => {
  const [activeTab, setActiveTab] = useState("overview");

  if (!fan) return null;

  // Mock relationship data if not present
  const followedCreators = fan.followedCreators || [
    { name: "Gigi Kiya", category: "Music", status: "Subscriber", tier: "Gold Fan", since: "2023-11-12", spend: 4500 },
    { name: "Comedian Eshetu", category: "Comedy", status: "Follower", tier: "Free", since: "2024-01-05", spend: 0 },
    { name: "Zeleke Gessesse", category: "Art", status: "Subscriber", tier: "Silver Fan", since: "2023-12-20", spend: 1200 },
  ];

  const recentActivity = fan.recentActivity || [
    { type: "Purchase", item: "Unity Concert Ticket", creator: "Gigi Kiya", amount: 1500, date: "2024-03-10" },
    { type: "Subscription", item: "Gold Monthly", creator: "Gigi Kiya", amount: 500, date: "2024-03-01" },
    { type: "Comment", item: "Loved the new video!", creator: "Comedian Eshetu", amount: 0, date: "2024-02-28" },
    { type: "Like", item: "New painting reveal", creator: "Zeleke Gessesse", amount: 0, date: "2024-02-25" },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <Card className="max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col p-0">
        {/* Header */}
        <div className="p-6 border-b border-slate-100 flex justify-between items-start bg-slate-50">
          <div className="flex gap-4">
            <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center">
              {fan.profilePictureUrl ? (
                <img src={fan.profilePictureUrl} alt={fan.name} className="w-16 h-16 rounded-full object-cover" />
              ) : (
                <User className="w-8 h-8 text-purple-600" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">{fan.name}</h2>
              <div className="flex gap-2 text-sm text-slate-600">
                <span>{fan.email}</span>
                <span>•</span>
                <span>Joined {new Date(fan.joinedDate).toLocaleDateString()}</span>
              </div>
              <div className="flex gap-2 mt-2">
                {fan.isCreator && (
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium border border-blue-200">
                    Also Creator
                  </span>
                )}
                <span className={`px-2 py-0.5 text-xs rounded-full font-medium border ${
                  fan.status === "Active" 
                    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                    : "bg-red-100 text-red-700 border-red-200"
                }`}>
                  {fan.status || "Active"}
                </span>
                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium border border-purple-200">
                  LTV: {fan.ltv || fan.totalSpend || 0} ETB
                </span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-slate-100 px-6">
          <div className="flex gap-6">
            {["overview", "creators", "activity", "financials"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? "border-purple-600 text-purple-700"
                    : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6 bg-white">
          {activeTab === "overview" && (
            <div className="space-y-6">
              {/* Key Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="p-4 bg-purple-50 rounded-xl border border-purple-100">
                  <div className="text-sm text-purple-600 mb-1">Total Spend</div>
                  <div className="text-2xl font-bold text-slate-800">{fan.totalSpend || 5450} ETB</div>
                </div>
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="text-sm text-blue-600 mb-1">Creators Followed</div>
                  <div className="text-2xl font-bold text-slate-800">{followedCreators.length}</div>
                </div>
                <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                  <div className="text-sm text-emerald-600 mb-1">Active Subs</div>
                  <div className="text-2xl font-bold text-slate-800">
                    {followedCreators.filter(c => c.status === "Subscriber").length}
                  </div>
                </div>
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                  <div className="text-sm text-amber-600 mb-1">Engagement Score</div>
                  <div className="text-2xl font-bold text-slate-800">8.5/10</div>
                </div>
              </div>

              {/* Creator Breakdown */}
              <div>
                <h3 className="text-lg font-bold text-slate-800 mb-4">Top Creator Relationships</h3>
                <div className="space-y-3">
                  {followedCreators.slice(0, 3).map((creator, index) => (
                    <div key={index} className="flex items-center justify-between p-4 border border-slate-100 rounded-xl hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                          <Music className="w-5 h-5 text-slate-500" />
                        </div>
                        <div>
                          <div className="font-medium text-slate-800">{creator.name}</div>
                          <div className="text-xs text-slate-500">{creator.category}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-sm font-medium text-slate-700">{creator.status}</div>
                          <div className="text-xs text-slate-500">{creator.tier}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium text-slate-700">{creator.spend} ETB</div>
                          <div className="text-xs text-slate-500">Lifetime Spend</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === "creators" && (
            <div className="space-y-4">
               <h3 className="text-lg font-bold text-slate-800 mb-4">All Followed Creators</h3>
               <div className="overflow-hidden border border-slate-200 rounded-xl">
                 <table className="w-full text-left text-sm">
                   <thead className="bg-slate-50 border-b border-slate-200">
                     <tr>
                       <th className="px-4 py-3 font-medium text-slate-700">Creator</th>
                       <th className="px-4 py-3 font-medium text-slate-700">Category</th>
                       <th className="px-4 py-3 font-medium text-slate-700">Status</th>
                       <th className="px-4 py-3 font-medium text-slate-700">Since</th>
                       <th className="px-4 py-3 font-medium text-slate-700 text-right">Spend</th>
                     </tr>
                   </thead>
                   <tbody className="divide-y divide-slate-100">
                     {followedCreators.map((creator, i) => (
                       <tr key={i} className="hover:bg-slate-50">
                         <td className="px-4 py-3 font-medium text-slate-800">{creator.name}</td>
                         <td className="px-4 py-3 text-slate-600">{creator.category}</td>
                         <td className="px-4 py-3">
                           <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${
                             creator.status === "Subscriber" 
                               ? "bg-purple-100 text-purple-700 border-purple-200" 
                               : "bg-slate-100 text-slate-600 border-slate-200"
                           }`}>
                             {creator.status}
                           </span>
                         </td>
                         <td className="px-4 py-3 text-slate-600">{creator.since}</td>
                         <td className="px-4 py-3 text-slate-800 font-medium text-right">{creator.spend} ETB</td>
                       </tr>
                     ))}
                   </tbody>
                 </table>
               </div>
            </div>
          )}
          
          {activeTab === "activity" && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-slate-800 mb-4">Recent Interactions</h3>
              <div className="space-y-4 relative before:absolute before:inset-y-0 before:left-6 before:w-px before:bg-slate-200">
                {recentActivity.map((activity, i) => (
                  <div key={i} className="relative flex gap-4 pl-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 z-10 border-2 border-white shadow-sm ${
                      activity.type === "Purchase" ? "bg-emerald-100 text-emerald-600" :
                      activity.type === "Subscription" ? "bg-purple-100 text-purple-600" :
                      "bg-blue-100 text-blue-600"
                    }`}>
                      {activity.type === "Purchase" ? <DollarSign className="w-4 h-4" /> : 
                       activity.type === "Subscription" ? <Heart className="w-4 h-4" /> : 
                       <Calendar className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 bg-slate-50 p-3 rounded-lg border border-slate-100">
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-medium text-slate-800">{activity.type}</span>
                        <span className="text-xs text-slate-500">{activity.date}</span>
                      </div>
                      <p className="text-sm text-slate-600">
                        {activity.type === "Purchase" ? `Purchased ${activity.item}` : 
                         activity.type === "Subscription" ? `Subscribed to ${activity.item}` : 
                         activity.item} 
                        <span className="font-medium text-slate-800"> • {activity.creator}</span>
                      </p>
                      {activity.amount > 0 && (
                        <div className="mt-2 text-sm font-bold text-slate-800">
                          -{activity.amount} ETB
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-between items-center">
          <div className="text-sm text-slate-500">
            User ID: <span className="font-mono">{fan.id}</span>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50">
              Suspend User
            </Button>
            <Button>Edit Profile</Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default FanDetailModal;
